import CSS from 'csstype';
import React, { useState } from 'react';

import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { fontSizing, ITProps, TKey, usePanel, usePublicaTranslation } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

const getBackgroundColor = (
  theme: Record<string, string>,
  backgroundColor: string | undefined,
  isHover: boolean = false,
  hoverColor?: string,
  clearBackground: boolean = false,
  disabled: boolean = false,
) => {
  switch (true) {
    case disabled || (clearBackground && isHover):
      return theme.primaryButtonDisabled;
    case clearBackground:
      return 'inherit';
    case isHover:
      return hoverColor || theme.primaryButtonHovered;
    default:
      return backgroundColor || theme.primaryButton;
  }
};

const getColor = (
  theme: Record<string, string>,
  color: string | undefined,
  clearBackground: boolean = false,
  disabled: boolean = false,
) => {
  switch (true) {
    case disabled:
      return publicaColors.gray;
    case clearBackground:
      return color || publicaColors.gray;
    default:
      return color || theme.primaryButtonText;
  }
};

const getButtonFontSize = (large?: boolean, small?: boolean): string => {
  switch (true) {
    case !!large:
      return fontSizing.large;
    case !!small:
      return fontSizing.small;
    default:
      return fontSizing.base;
  }
};

interface IButtonProps {
  large?: boolean;
  small?: boolean;
  clearBackground?: boolean;
  backgroundColor?: string;
  hoverColor?: string;
  color?: string;
  onClick: (props?: any | null) => void;
  disabled?: boolean;
  allCaps?: boolean;
  enableTextWrap?: boolean;
  style?: CSS.Properties;
  className?: string;
  children?: any;
  tKey?: TKey; // tKey will override children if provided
  tProps?: ITProps; // translation properties for the button text
}

export const ButtonV2 = ({
  large,
  small,
  clearBackground,
  backgroundColor,
  hoverColor,
  color,
  onClick,
  disabled,
  allCaps,
  enableTextWrap,
  style,
  className = '',
  children,
  tKey,
  tProps = {},
}: IButtonProps) => {
  const { theme } = usePanel();
  const { t } = usePublicaTranslation();
  const [isHover, setIsHover] = useState(false);

  const fontColor = getColor(theme, color, clearBackground, disabled);
  const textTransform = allCaps ? 'uppercase' : 'inherit';
  const whiteSpace = enableTextWrap ? 'normal' : 'nowrap';

  return (
    <button
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={disabled ? () => null : onClick}
      className={className}
      style={{
        display: 'inline-block',
        borderWidth: 0,
        lineHeight: 'inherit',
        fontFamily: 'inherit',
        fontStyle: 'inherit',
        textTransform,
        whiteSpace,
        margin: 0,
        padding: '8px 16px',
        borderRadius: '4px',
        backgroundColor: getBackgroundColor(
          theme,
          backgroundColor,
          isHover,
          hoverColor,
          clearBackground,
          disabled,
        ),
        fontSize: getButtonFontSize(large, small),
        fontWeight: FONT_WEIGHT.bold,
        color: fontColor,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {tKey ? t(tKey, tProps) : children}
    </button>
  );
};
